<template>
  <Menu
    ref="dropdown"
    as="div"
    class="dropdown">
    <div>
      <MenuButton
        class="dropdown__button">
        <slot>
          {{ label }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            viewBox="0 0 512 512">
            <path
              fill="currentColor"
              d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
          </svg>
        </slot>
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95">
      <MenuItems
        class="dropdown__items"
        :style="style">
        <div class="dropdown__items-container">
          <MenuItem
            v-for="(item, index) in options"
            v-slot="{ active }"
            :key="index">
            <component
              :is="item.href ? NuxtLink : 'button'"
              :to="item.href"
              class="dropdown__menu-item"
              :class="{ 'dropdown__menu-item--active': active }"
              @click="onItemClicked(item)">
              <slot
                name="item"
                :item="item">
                <span>
                  <AIcon
                    v-if="itemPrefixIcon"
                    :icon="itemPrefixIcon"
                    class="dropdown__menu-item--prefix" />
                  {{ item.label }}
                </span>
                <span
                  v-if="showSelectedIcon && selectedItem === item.label"
                  class="dropdown__menu-item--selected">
                  <AIcon icon="fa:regular:check" />
                </span>
              </slot>
            </component>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { onMounted, onUnmounted, computed, ref, watch } from "vue";
import type { DropdownOption } from "../../../types";
import { NuxtLink } from "#components";

const props = withDefaults( defineProps<{
  label?: string | null;
  options: DropdownOption[];
  position?: "up" | "down" | "auto";
  emitSelectedOption?: boolean;
  itemPrefixIcon?: string | null;
}>(), {
  label: null,
  position: "auto",
  emitSelectedOption: false,
  itemPrefixIcon: null,
} );

const emit = defineEmits<{
  ( event: "item-selected", value: any ): void;
}>();

const dropdown = ref();
const up = ref( false );
const selectedItem = ref( "" );
const showSelectedIcon = computed( () => props.emitSelectedOption && selectedItem.value !== "" );

function onItemClicked ( item: DropdownOption ) {
  if ( item.onClick ) {
    item.onClick();
  } else if ( props.emitSelectedOption ) {
    selectedItem.value = item.label;
    emit( "item-selected", selectedItem.value );
  }
}

const style = computed( () => {
  const result: Record<string, string> = {};
  if ( up.value ) {
    result.bottom = "100%";
  }

  return result;
} );

onMounted( () => {
  if ( props.position === "auto" ) {
    calculatePosition();
    window.addEventListener( "resize", calculatePosition );
  }
} );

onUnmounted( () => {
  if ( props.position === "auto" ) {
    window.removeEventListener( "resize", calculatePosition );
  }
} );

watch( () => props.position, ( val ) => {
  if ( val === "auto" ) {
    calculatePosition();
    window.addEventListener( "resize", calculatePosition );
  } else {
    up.value = val === "up";
    window.removeEventListener( "resize", calculatePosition );
  }
} );

function calculatePosition () {
  const bottom = dropdown.value.$el.getBoundingClientRect().bottom;
  const windowHeight = window.innerHeight;
  const padding = 44 * ( props.options.length + 1 );

  up.value = bottom > windowHeight - padding;
}
</script>

<style scoped lang="scss">
.dropdown {
  position: relative;
  display: inline-block;
  text-align: left;

  &__button {
    display: inline-flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    column-gap: .5rem;
    border-radius: var(--border-radius);
    background: #fff;
    padding: .5rem .75rem;
    font-size: .875rem;
    line-height: 1.25rem;
    border: none;
    box-shadow: rgb(255, 255, 255) 0 0 0 0 inset, rgb(209, 213, 219) 0 0 0 1px inset, rgba(0, 0, 0, 0.05) 0 1px 2px 0;
    font-weight: 600;

    &:hover {
      cursor: pointer;
      background: #F8F8F8;
    }
  }

  &__items {
    position: absolute;
    right: 0;
    z-index: 10;
    transform-origin: bottom left;
    background: #fff;
    margin-top: .5rem;
    margin-bottom: .5rem;
    border-radius: var(--border-radius);
    width: 14rem;
    box-shadow: rgb(255, 255, 255) 0 0 0 0, rgba(0, 0, 0, 0.05) 0 0 0 1px, rgba(0, 0, 0, 0.1) 0 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -4px;

    &-container {
      padding: .25rem 0;
    }
  }

  &__menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .5rem 1rem;
    text-decoration: none;
    font-size: .875rem;
    line-height: 1.25rem;
    width: 100%;
    background: #fff;
    border: none;
    text-align: left;

    &:hover {
      background: #F8F8F8;
    }

    &--active {

    }

    &--prefix {
      margin-right: 0.5rem;
    }
  }
}
</style>
